import { Badge } from '@lombok/ui';
import type { BadgeTone } from '@lombok/ui';
import {
  AccountStatus,
  InvoiceStatus,
  OrderStatus,
  PaymentStatus,
  QuoteStatus,
} from '@lombok/shared';

/**
 * Domain status -> Spanish label + consistent color tone. Centralizing this map
 * keeps status colors identical everywhere (a docs/11 UX requirement). Stored
 * values stay snake_case; only the label shown to users is Spanish.
 */

type StatusKind = 'order' | 'account' | 'payment' | 'invoice' | 'quote';

interface StatusMeta {
  label: string;
  tone: BadgeTone;
}

const ORDER_MAP: Record<OrderStatus, StatusMeta> = {
  draft: { label: 'Borrador', tone: 'neutral' },
  submitted: { label: 'Enviado', tone: 'info' },
  pending_approval: { label: 'Pendiente de aprobación', tone: 'warning' },
  approved: { label: 'Aprobado', tone: 'success' },
  rejected: { label: 'Rechazado', tone: 'danger' },
  picking: { label: 'En preparación', tone: 'info' },
  dispatched: { label: 'Despachado', tone: 'info' },
  delivered: { label: 'Entregado', tone: 'success' },
  cancelled: { label: 'Cancelado', tone: 'neutral' },
  closed: { label: 'Cerrado', tone: 'neutral' },
};

const ACCOUNT_MAP: Record<AccountStatus, StatusMeta> = {
  pending: { label: 'En revisión', tone: 'warning' },
  active: { label: 'Activo', tone: 'success' },
  on_hold: { label: 'En espera', tone: 'warning' },
  suspended: { label: 'Suspendido', tone: 'danger' },
  rejected: { label: 'Rechazado', tone: 'danger' },
  closed: { label: 'Cerrado', tone: 'neutral' },
};

const PAYMENT_MAP: Record<PaymentStatus, StatusMeta> = {
  draft: { label: 'Borrador', tone: 'neutral' },
  pending: { label: 'En revisión', tone: 'warning' },
  approved: { label: 'Aprobado', tone: 'success' },
  rejected: { label: 'Rechazado', tone: 'danger' },
  refunded: { label: 'Reembolsado', tone: 'info' },
  cancelled: { label: 'Cancelado', tone: 'neutral' },
};

const INVOICE_MAP: Record<InvoiceStatus, StatusMeta> = {
  draft: { label: 'Borrador', tone: 'neutral' },
  issued: { label: 'Emitida', tone: 'info' },
  partially_paid: { label: 'Pago parcial', tone: 'warning' },
  paid: { label: 'Pagada', tone: 'success' },
  overdue: { label: 'Vencida', tone: 'danger' },
  void: { label: 'Anulada', tone: 'neutral' },
};

const QUOTE_MAP: Record<QuoteStatus, StatusMeta> = {
  draft: { label: 'Borrador', tone: 'neutral' },
  sent: { label: 'Enviada', tone: 'info' },
  accepted: { label: 'Aceptada', tone: 'success' },
  rejected: { label: 'Rechazada', tone: 'danger' },
  expired: { label: 'Expirada', tone: 'neutral' },
  converted: { label: 'Convertida', tone: 'success' },
};

const MAPS: Record<StatusKind, Record<string, StatusMeta>> = {
  order: ORDER_MAP,
  account: ACCOUNT_MAP,
  payment: PAYMENT_MAP,
  invoice: INVOICE_MAP,
  quote: QUOTE_MAP,
};

export interface StatusPillProps {
  kind: StatusKind;
  value: string;
}

export function StatusPill({ kind, value }: StatusPillProps) {
  const meta = MAPS[kind][value] ?? { label: value, tone: 'neutral' as BadgeTone };
  return <Badge tone={meta.tone}>{meta.label}</Badge>;
}

/**
 * Resolve a domain status value to its centralized Spanish label (raw value as
 * fallback) WITHOUT rendering a pill. Lets list/report views that show a status as
 * plain text reuse the same single source of truth as {@link StatusPill}.
 */
export function statusLabel(kind: StatusKind, value: string): string {
  return MAPS[kind][value]?.label ?? value;
}

/** Order-status → Spanish label (convenience over {@link statusLabel}). */
export function orderStatusLabel(value: string): string {
  return statusLabel('order', value);
}
